import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Stack,
  Button,
  Flex,
  Center,
  Badge
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import {
  useMergeDetails,
  useApproveMerge,
  useRejectMerge
} from 'hooks/queries/useMerges';
import Loader from 'components/Loader/Loader';
import ItemCard from 'components/Item/ItemCard/ItemCard';
import ItemGrid from 'components/Item/ItemGrid/ItemGrid';
import { NavigationService } from 'services';
import { ItemModel } from 'models';
import { ProcessStatus } from 'models/types';

export default function MergeDetailsView() {
  const { mergeId } = useParams();
  const navigate = useNavigate();

  const handleMergedItemClick = (item: ItemModel, isDemoted: boolean) => {
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName(), isDemoted));
  };

  const handleBaseItemClick = (item: ItemModel, isDemoted: boolean) => {
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName(), isDemoted));
  };

  // React Query mutations
  const approveMutation = useApproveMerge();
  const rejectMutation = useRejectMerge();

  const { data: merge, isLoading, isError } = useMergeDetails(mergeId || '');

  // Determine if items are demoted based on merge status
  const isMergedItemDemoted = merge?.status === ProcessStatus.PENDING;
  const areBaseItemsDemoted = merge?.status === ProcessStatus.APPROVED;

  // Handlers with actual mutation logic
  const handleApprove = () => {
    if (!mergeId) return;
    approveMutation.mutate(mergeId);
  };

  const handleReject = () => {
    if (!mergeId) return;
    rejectMutation.mutate(mergeId);
  };

  if (isLoading) return <Loader />;
  if (isError || !merge) return <Center p={8}><Text>Failed to load merge details.</Text></Center>;

  return (
    <Box maxW="1200px" mt="100px" p={4}>
      <Stack spacing={6}>
        {/* Merged Item and Merge Overview */}
        <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
          <Box flex="1">
            <Card>
              <CardHeader bg="blue.500" color="white">
                <Heading size="md">Merged Item {isMergedItemDemoted && <Badge colorScheme="orange" ml={2}>Demoted</Badge>}</Heading>
              </CardHeader>
              <CardBody>
                <ItemCard
                  item={merge.mergedItem}
                  size="md"
                  onClick={() => handleMergedItemClick(merge.mergedItem, isMergedItemDemoted)}
                  clickOnImage
                  clickOnName
                  showMetadata
                  defaultMetadataExpanded={true}
                />
              </CardBody>
            </Card>
          </Box>
          <Box flex="2">
            <Card>
              <CardHeader bg="rose.500" color="white">
                <Heading size="md">Merge Overview</Heading>
              </CardHeader>
              <CardBody>
                <Stack spacing={2}>
                  <Text><b>Merge ID:</b> {merge.id}</Text>
                  <Text>
                    <b>Status:</b>{' '}
                    <Badge
                      colorScheme={
                        merge.status === ProcessStatus.APPROVED ? 'green' :
                        merge.status === ProcessStatus.REJECTED ? 'red' :
                        'blue'
                      }
                    >
                      {merge.status}
                    </Badge>
                  </Text>
                  <Text><b>Number of Base Items:</b> {merge.baseItems.length}</Text>
                  <Text><b>Merged Item:</b> {merge.mergedItem.getDisplayName()}</Text>
                </Stack>
              </CardBody>
            </Card>
            {/* Actions */}
            <Card mt={4}>
              <CardHeader bg="gray.100">
                <Heading size="sm">Merge Actions</Heading>
              </CardHeader>
              <CardBody>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <Button
                    leftIcon={<CheckIcon />}
                    colorScheme="green"
                    onClick={handleApprove}
                    isLoading={approveMutation.isPending}
                    isDisabled={merge.status === ProcessStatus.APPROVED}
                  >
                    Approve
                  </Button>
                  <Button
                    leftIcon={<CloseIcon />}
                    colorScheme="red"
                    onClick={handleReject}
                    isLoading={rejectMutation.isPending}
                    isDisabled={merge.status === ProcessStatus.REJECTED}
                  >
                    Reject
                  </Button>
                </Stack>
              </CardBody>
            </Card>
          </Box>
        </Flex>
        {/* Base Items Grid */}
        <Card>
          <CardHeader bg="gray.100">
            <Heading size="sm">
              Base Items ({merge.baseItems.length})
              {areBaseItemsDemoted && <Badge colorScheme="orange" ml={2}>Demoted</Badge>}
            </Heading>
          </CardHeader>
          <CardBody>
            {merge.baseItems.length === 0 ? (
              <Text>No base items in this merge.</Text>
            ) : (
              <ItemGrid
                items={merge.baseItems}
                onItemNameClick={(item) => handleBaseItemClick(item, areBaseItemsDemoted)}
                isLoading={false}
                showFilters={false}
                paginate={false}
                showMetadata={true}
                defaultMetadataExpanded={true}
              />
            )}
          </CardBody>
        </Card>
      </Stack>
    </Box>
  );
}
